/**
 * MFA messages for react-intl.
 * All defaultMessage and description values copied verbatim from
 * ss-ui-client/src/global/step-up-authentication/messages.js
 */

import { defineMessages, type MessageDescriptor } from 'react-intl';

export const mfaMessages = defineMessages({
  mfaModalTitle: {
    defaultMessage: 'Multi-Factor Authentication Required',
    id: 'WcY9Pc',
    description: 'auth0 mfa modal title',
  },
  passwordTitle: {
    defaultMessage: 'Enter Your Password',
    id: 'qaqIUd',
    description: 'prompt for a user to enter their password into a field',
  },
  passwordSubheader: {
    defaultMessage: 'To manage your multi-factor authentication settings, you must first verify your account.',
    id: 'S8+35R',
    description: 'explainer text for the first step in a series of identity verification steps',
  },
  secondSessionModalContext: {
    defaultMessage: 'Please re-enter your password in order to establish a phone number for multi-factor authentication.',
    id: 'sGBN25',
    description: 'contextual explanation shown when user is in second session modal flow for phone verification',
  },
  forcedMfaFlowContext: {
    defaultMessage:
      'In order to continue using ShipStation and keep your account secure, ShipStation uses Two-Factor Authentication (2FA).',
    id: 'OiiU61',
    description: 'contextual explanation shown when user is in forced MFA flow to explain why MFA setup is required',
  },
  lastMfaClaimSubheader: {
    defaultMessage: 'To continue, you must first verify your account.',
    id: 'lNHUvF',
    description: 'explainer text for the first step in a series of identity verification steps',
  },
  passwordSubheader2: {
    defaultMessage: 'Enter your password to get started.',
    id: 'G0WjBV',
    description: 'direction on how to start step-up authentication',
  },
  username: {
    defaultMessage: 'Username',
    id: 'L0LdeL',
    description: 'label for username',
  },
  password: {
    defaultMessage: 'Password',
    id: 'ww7FoJ',
    description: 'label for password input',
  },
  togglePasswordAriaLabel: {
    defaultMessage: "button to toggle the password input's visibility",
    id: 'DbEoW5',
    description: 'label for password input',
  },
  forgotPassword: {
    defaultMessage: 'Forgot Password?',
    id: 'pCVekD',
    description: 'text for a link that takes users to a password reset page',
  },
  cancelButton: {
    defaultMessage: 'Cancel',
    id: 'T5fKIK',
    description: 'button text to cancel an action',
  },
  submitButton: {
    defaultMessage: 'Submit',
    id: 'wvyhNw',
    description: 'button text to submit form data',
  },
  sendCodeButton: {
    defaultMessage: 'Send code',
    id: 'updgwj',
    description: 'text for a button that when clicked will send the user a verification code',
  },
  selectDifferentMethodButton: {
    defaultMessage: 'Select a different method',
    id: 'ETZdqd',
    description: 'button text to select a different method of authentication',
  },
  chooseTitle: {
    defaultMessage: 'Select a Verification Method',
    id: 'ld2Att',
    description: 'title for mfa enrollment choice page',
  },
  chooseSubheader: {
    defaultMessage: 'Select how you want to receive your verification code:',
    id: 'EiJkZX',
    description: 'direction on how to continue step-up authentication',
  },
  forcedMfaChooseSubheader: {
    defaultMessage: 'To get started we will send a one-time code to the email address on file:',
    id: 'C4J0Jj',
    description: 'direction for forced MFA flow explaining email code will be sent',
  },
  securityExplanation: {
    defaultMessage: 'This extra layer of protection helps ensure only you can access your account.',
    id: 'tEYk4P',
    description: 'explanation of why MFA security is important for user account protection',
  },
  emailEnrollment: {
    defaultMessage: 'Email me at: {maskedEmail}',
    id: 'S2EA6Z',
    description: 'display text for email mfa enrollments',
  },
  smsEnrollment: {
    defaultMessage: 'Text me at: (***) *** - {lastFour}',
    id: 'JwG9as',
    description: 'display text for sms mfa enrollments',
  },
  voiceEnrollment: {
    defaultMessage: 'Call me at: (***) *** - {lastFour}',
    id: 'e6ZV/p',
    description: 'display text for phone mfa enrollments',
  },
  totpEnrollment: {
    defaultMessage: "Use your authenticator app's generated code",
    id: 'Uzxutq',
    description: 'display text for phone mfa enrollments',
  },
  challengeEmailTitle: {
    defaultMessage: 'Check Your Email',
    id: 'Z8e7pg',
    description: 'instructions telling a user to check their email',
  },
  challengePhoneTitle: {
    defaultMessage: 'Check Your Phone',
    id: '8EsBvH',
    description: 'instructions telling a user to check their phone',
  },
  challengeAuthenticatorTitle: {
    defaultMessage: 'Check Your TOTP App',
    id: 'vg4660',
    description: 'instructions telling a user to check their TOTP (temporary one-time password) application',
  },
  challengeEmailSubheader: {
    defaultMessage: 'Enter the 6 digit verification code sent to the email address below.',
    id: 'c0KWdA',
    description: 'direction on completing an email challenge',
  },
  challengeSmsSubheader: {
    defaultMessage: 'Enter the 6 digit verification code sent to the sms number below.',
    id: 'lGxU9D',
    description: 'direction on completing an sms challenge',
  },
  challengeVoiceSubheader: {
    defaultMessage: 'Enter the 6 digit verification code from the automated call made to the number below.',
    id: 'vVeppp',
    description: 'direction on completing a voice multi-facor authentication challenge',
  },
  challengeAuthenticatorSubheader: {
    defaultMessage: 'Enter the 6 digit one-time password from your authenticator app.',
    id: 'V9srqw',
    description: 'direction on completing a totp multi-facor authentication challenge',
  },
  invalidCodeError: {
    defaultMessage: 'Invalid code entered',
    id: 'jXne30',
    description: 'error message when mfa code entered incorrectly',
  },
  errorMessage: {
    defaultMessage: 'An error occurred {message}. Code: {code}',
    id: '3OOW7f',
    description: 'error message base content',
  },
  expiryNotice: {
    defaultMessage: 'Your verification code will expire in 5 minutes.',
    id: 'z5w4t+',
    description: 'how long the mfa code is valid for',
  },
  resendCode: {
    defaultMessage: "Didn't receive the code? <a>Resend code</a>",
    id: 'Xch9zJ',
    description: 'text to tell user how to resend the mfa code',
  },
  resendCodeError: {
    defaultMessage: 'Failed to resend verification code',
    id: 'csPgxY',
    description: 'error text if resending mfa code fails',
  },
  mfaIsNeededNotification: {
    defaultMessage: 'Account verification needed to continue.',
    id: 'jOw9fR',
    description: 'shown to user when a request fails because they need to complete an MFA challenge',
  },
  mfaVerificationInProgress: {
    defaultMessage: 'Updating session information...',
    id: 'D0i/5f',
    description: 'shown to user when they have completed mfa and we are updating their session info',
  },
  mfaVerificationSuccess: {
    defaultMessage: 'Session information updated.',
    id: '+xL+bL',
    description: 'shown to user when they have completed mfa and we have updated their session info',
  },
  mfaVerificationFailure: {
    defaultMessage: 'Verification succeeded but failed to update session. <click>Click here</click> to retry.',
    id: 'VSPBsL',
    description: "shown if we failed to update the user's session information",
  },
});

/**
 * Message sets differing only in passwordSubheader.
 */
export const mfaMessageSets = {
  mfaSettingsMessages: {
    ...mfaMessages,
    passwordSubheader: mfaMessages.passwordSubheader,
  } as Record<string, MessageDescriptor>,
  lastMfaClaimMessages: {
    ...mfaMessages,
    passwordSubheader: mfaMessages.lastMfaClaimSubheader,
  } as Record<string, MessageDescriptor>,
} as const;

export type MfaMessageSet = (typeof mfaMessageSets)[keyof typeof mfaMessageSets];

/**
 * Messages for the native email verification flow.
 */
export const nativeAuthMessages = defineMessages({
  primaryAction: {
    defaultMessage: "I've verified my email",
    id: '96xPam',
    description: 'continue button text for the native account verification needed modal',
  },
});
